import React, { useMemo ,useState, useEffect } from "react";
import styled from '@emotion/styled';
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { useStaticQuery, graphql } from "gatsby"
import RoundButton from '../../../components/RoundButton'
import { DateTime } from 'luxon';
import { useLocalization } from "gatsby-theme-i18n";
import * as breakpoint from '../../../styles/breakpoints.module.scss';
import { useCurrentTime } from '../../../functions/useCurrentTime';

const pad = function(string, n) {
  return Array(n-string.length+1).join(0) + string;
}

const CountdownSection = () => {
  const { currentTime } = useCurrentTime();

  const EventStarttime = useMemo(() => DateTime.fromISO('2021-05-15T10:00:00+09:00'),[]);
  const [ Diff, setDiff ] = useState(
    EventStarttime.diff(currentTime, [ 'days', 'hours', 'minutes', 'seconds']).toObject()
  );
  const [isHide, setHide] = useState(EventStarttime.diff(currentTime) < 0);  

  const data = useStaticQuery(graphql`
    query CountdownSection {
      deco: file(sourceInstanceName: {eq: "images"}, relativePath: {eq: "layout/deco_countdown1.jpg"}) {
        childImageSharp {
          gatsbyImageData(
            layout: FULL_WIDTH
            placeholder: NONE
          )
        }
      }
    }
  `)

  useEffect(() => {
    if(isHide)
      return;

    if(EventStarttime.diff(currentTime) >= 0)
    {
      setDiff(
        EventStarttime.diff(currentTime, [ 'days', 'hours', 'minutes', 'seconds']).toObject()
      );
    } else
    {
      setHide(true);
    }

  },[EventStarttime, currentTime, isHide ])

  
  const { t, currentLocaleObj } = useLocalization();

  if(isHide)
    return(<React.Fragment></React.Fragment>)

  return(
    <SectionWrapper>
      <CountdownWrapper>
        <CountdownHeadline>
          <span>{t('home.countdown.label')}</span>
          <span>{t('home.countdown.title')}</span>
        </CountdownHeadline>
        <Counter>
          <Digit isRed={true}>
            <span>{pad(Diff.days.toString(),2)}</span>
            <span>{t('home.countdown.days.label')}</span>
          </Digit>
          <Digit isDivider={true}>
            <span>:</span>
          </Digit>
          <Digit>
            <span>{pad(Diff.hours.toString(),2)}</span>
            <span>{t('home.countdown.hours.label')}</span>
          </Digit>
          <Digit isDivider={true}>
            <span>:</span>
          </Digit>
          <Digit>
            <span>{pad(Diff.minutes.toString(),2)}</span>
            <span>{t('home.countdown.minutes.label')}</span>
          </Digit>
          <Digit isDivider={true}>
            <span>:</span>
          </Digit>
          <Digit>
            <span>{pad(Math.floor(Diff.seconds).toString(),2)}</span>
            <span>{t('home.countdown.seconds.label')}</span>
          </Digit>
        </Counter>
        <ButtonContainer>
          <CounterButton size={'large'} color={'#4E4E50'} fade to={`/${currentLocaleObj.prefix}/about/`}>
            {t('home.about.button.label')}
          </CounterButton>
          <CounterButton size={'large'} color={'#F50057'} fade to={`/${currentLocaleObj.prefix}/streaming/`}>
            {t('home.streaming.button.label')}
          </CounterButton>
        </ButtonContainer>
        <Decoration>
          <GatsbyImage image={getImage(data.deco)} alt="deco" />
        </Decoration>
      </CountdownWrapper>
    </SectionWrapper>
  );
}

export default CountdownSection;

const SectionWrapper = styled.div`
  background: #F7F7FA;
  padding:96px 0 96px;
  @media only screen and (max-width: ${breakpoint.smMax}){
    padding:64px 0;
  }
  @media only screen and (max-width: ${breakpoint.xsMax}){
    padding:32px 0;
  }
`

const CountdownWrapper = styled.div`
  width:928px;
  margin:0 auto;
  position:relative;
  @media only screen and (max-width: ${breakpoint.smMax}){
    width:100%;
    padding:0 80px;
  }
  @media only screen and (max-width: ${breakpoint.xsMax}){
    padding:0 24px;
  }
`

const CountdownHeadline = styled.div`
  margin-bottom:32px;
  padding-right:140px;
  > span{
    display:block;
  
    &:nth-of-type(1){
      color: #D00F31;
      font-size: 24px;
      line-height: 32px;
      font-weight: 900;
      letter-spacing: 0.05em;
    }

    &:nth-of-type(2){
      color: #2C2C2E;
      font-weight: 900;
      font-size: 48px;
      line-height: 56px;
      letter-spacing: 0.01em;
    }
  }

  @media only screen and (max-width: ${breakpoint.smMax}){
    margin-bottom:28px;
    padding-right:80px;
    > span{
      &:nth-of-type(1){
        font-size: 12px;
        line-height: 16px;
      }

      &:nth-of-type(2){
        font-size: 24px;
        line-height: 32px;
      }
    }
  }

  @media only screen and (max-width: ${breakpoint.xsMax}){
    margin-bottom:22px;
    padding-right:0;
    > span{
      &:nth-of-type(1){
        font-size: 10px;
        line-height: 14px;
      }

      &:nth-of-type(2){
        font-size: 20px;
        line-height: 28px;
      }
    }
  }
`

const Counter = styled.div`
  background: #FFFFFF;
  border-radius: 32px 12px;
  height:208px;
  padding-top:72px;
  display:flex;
  justify-content:center;

  @media only screen and (max-width: ${breakpoint.smMax}){
    padding-top:48px;
    height:160px;
  }
  @media only screen and (max-width: ${breakpoint.xsMax}){
    padding-top:37px;
    height:128px;
  }
`

const Decoration = styled.div`
  width:256px;
  height:256px;
  position:absolute;
  top:0;
  right:-128px;

  @media only screen and (max-width: ${breakpoint.smMax}){
    width:128px;
    height:128px;
    right:16px;
    bottom: 169px;
    top: auto;
  }
  @media only screen and (max-width: ${breakpoint.xsMax}){
    display:none;
  }
`

const ButtonContainer = styled.div`
  text-align:center;
  margin-top:32px;
  @media only screen and (max-width: ${breakpoint.xsMax}){
    margin-top:16px;
  }
`

const Digit = styled.div`
  ${props => props.isDivider ? 'width:auto;' : 'min-width:64px;'}
  > span{
    display:block;
    

    &:nth-of-type(1){
      font-weight: 900;
      font-size: 42.6667px;
      line-height: 42px;
      letter-spacing: 0.02em;
      ${props => props.isRed ? 'color: #D00F31;' : 'color: #2C2C2E;'}
      text-align:center;
    }

    &:nth-of-type(2){
      text-align:center;
      font-size: 14.6667px;
      line-height: 21px;
      letter-spacing: 0.015em;
      color: #2C2C2E;
    }
  }

  @media only screen and (max-width: ${breakpoint.xsMax}){
    ${props => props.isDivider ? 'width:auto;' : 'min-width:53px;'}
    > span{
      
      &:nth-of-type(1){
        font-size: 37.3333px;
        line-height: 32px;
      }
      &:nth-of-type(2){
        font-size: 12px;
        line-height: 16px;
      }
    }
    
  }
`

const CounterButton = styled(RoundButton)`
  
  font-size: 28px;
  line-height: 32px;
  height:auto;

  ${props => props.color === "#4E4E50" ? `padding: 14px 30px;` : `padding:16px 32px;`}

  &:last-of-type{
    margin-left:24px;
  }

  @media only screen and (max-width: ${breakpoint.smMax}){
    font-size: 17px;
    line-height: 24px;
    height:auto;
    ${props => props.color === "#4E4E50" ? `padding: 10px 22px;` : `padding:12px 24px;`}

  }

  @media only screen and (max-width: ${breakpoint.xsMax}){
    font-size: 14px;
    line-height: 20px;
    display:block;
    height:auto;
    padding-top:8px;
    padding-bottom:8px; 
    width: 100%;
    border-radius: 32px;

    &:last-of-type{
      margin-left:0px;
    }

    ${props => props.color === "#4E4E50" ? `margin-bottom:8px;` : ``}
  }
`
